/**
 * @author Fernando Ruano
 * @file saga.js
 * @description Patient Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import calendarService from '@nephro/services/calendar-service';

// Constants
import {
  CREATE_PATIENT_APPOINTMENTS,
  GET_ALL_PATIENT_APPOINTMENTS,
  GET_APPOINTMENT_AVAILABILITIES,
  RESCHEDULE_PATIENT_APPOINTMENTS,
  CANCEL_PATIENT_APPOINTMENTS,
  GET_AGENDA_MATE_PATIENT
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get patient appointments from backend
 * @param { object } action - Action with payload data
 */
export function* getPatientAppointmentsArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { patientId } = data;

  try {
    const response = yield call(
      calendarService.getPatientAppointments,
      patientId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to create patient appointment
 * @param { object } action - Action with payload data
 */
export function* createPatientAppointments(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;

  const { capId, patientId, typeQuote } = data;

  try {
    const response = yield call(
      calendarService.scheduleAppointment,
      capId,
      patientId,
      typeQuote
    );
    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to reschedule patient appointment
 * @param { object } action - Action with payload data
 */
export function* reSchedulePatientAppointments(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;

  const { capId, patientId, quoteId, typeQuote } = data;

  try {
    const response = yield call(
      calendarService.reScheduleAppointment,
      capId,
      patientId,
      quoteId,
      typeQuote
    );
    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to cancel patient appointment
 * @param { object } action - Action with payload data
 */
export function* cancelPatientAppointments(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;

  const { quoteId } = data;

  try {
    const response = yield call(calendarService.cancelAppointment, quoteId);
    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get appointment availabilities
 * @param { object } action - Action with payload data
 */
export function* getAppointmentAvailabilities(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { modalityId, patientCityId, patientId, quoteTypeId, desiredDate } =
    data;
  try {
    const response = yield call(
      calendarService.getAppointmentAvailability,
      modalityId,
      patientCityId,
      patientId,
      quoteTypeId,
      desiredDate
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get agendaMate patient info
 * @param { object } action - Action with payload data
 */
export function* getAgendaMatePatient(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { documentNumber, documentTypeId } = data;
  try {
    const response = yield call(
      calendarService.getAgendaMatePatient,
      documentNumber,
      documentTypeId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}
/**
 * Declare all component sagas
 */
export function* watchCalendarSaga() {
  yield takeLatest(GET_ALL_PATIENT_APPOINTMENTS, getPatientAppointmentsArray);
  yield takeLatest(CREATE_PATIENT_APPOINTMENTS, createPatientAppointments);
  yield takeLatest(
    GET_APPOINTMENT_AVAILABILITIES,
    getAppointmentAvailabilities
  );
  yield takeLatest(CANCEL_PATIENT_APPOINTMENTS, cancelPatientAppointments);
  yield takeLatest(
    RESCHEDULE_PATIENT_APPOINTMENTS,
    reSchedulePatientAppointments
  );
  yield takeLatest(GET_AGENDA_MATE_PATIENT, getAgendaMatePatient);
}
